import React, { useState } from "react";
import { updateUserDisclaimerStatus } from "../../lib/redux/actions";
import toast from "../../lib/utils/toast";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  Link,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types"; 
export const DisclaimerDialog = ({ isOpen, onClose }) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleAccept = () => {
    if (!agreed) {
      toast.error("Please agree to the terms and conditions.");
      return;
    }

    setLoading(true);
    updateUserDisclaimerStatus()
      .then((response) => {
        if (response.data.isDisclaimerAccepted === true) {
          toast.success("Disclaimer accepted!");
          onClose();
        } else {
          toast.error("Failed to accept disclaimer.");
        }
      })
      .catch(() => {
        toast.error("An error occurred while accepting the disclaimer.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Dialog open={isOpen} maxWidth="sm" fullWidth>
      <DialogContent className="flex flex-col gap-6 p-8 max-h-[80vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <div className="text-left">
          <Typography variant="h5" className="font-bold">
            Dabur GPT USER POLICY
          </Typography>
        </div>
        <Divider />

        <div className="space-y-6 text-gray-700">
          <Typography component="div">
            <p className="text-base">
              Dabur GPT's artificial intelligence (AI) generated content and responses are meant to provide general insights for user convenience. This information should not be used in place of expert counsel, human knowledge, or validated data. Before acting or making decisions based on any information acquired through Dabur GPT, we strongly advise users to confirm it independently for its accuracy, completeness, lack of bias and adherence to Company policies. AI-generated content may not always reflect the nuanced assessment of a human specialist since the content will always be aligned to OpenAI's LLM (Large Language Model). Dabur GPT, its parent company, and its affiliates of Dabur makes no guarantees about the accuracy, reliability, or completeness of the AI tool's output and to the fullest extent permitted by applicable law, Dabur India Limited and its affiliates disclaims any liability for damages, losses, or consequences arising from the use of or reliance on the information provided by Dabur GPT.
            </p>

            <Typography variant="h6" className="mt-6 font-bold">
              Terms & Conditions
            </Typography>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              1. Commitment to Responsible Use
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>You agree to use Dabur GPT in a lawful, ethical, and responsible manner, in full compliance with all applicable laws, regulations and Dabur's Policies including the DPDP Act and Intellectual Property Laws and refrain from using it for any illegal, deceptive, harmful, or unethical activities such as creating and spreading misinformation, obscenity, promote hate speech or engaging in fraudulent conduct that violates ethical standards or misuses Dabur's brand.</li>
              <li>You acknowledge that Dabur GPT is an AI-powered tool and not a human expert. Its outputs are generated based on data and algorithms only to assist, and may not always be accurate, complete, or current.</li>
              <li>Use of unapproved AI tools is strictly prohibited.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              2. Protecting Your Privacy
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Do not input/upload any sensitive, confidential information, trade secrets, or proprietary business information into AI tools without explicit written permission from your Head of Department (HoD). This includes, but is not limited to, financial data, customer information, trade secrets, and Personal Data (yours or others) such as Aadhaar numbers, passwords, contact details, financial information.</li>
              <li>Dabur GPT is not built to securely handle sensitive personal information.</li>
              <li>Please click on the link to read, understand and agree to the Privacy Notice</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              3. Usage Scope and Access
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Dabur GPT is strictly for internal use by authorised Dabur personnel only for business related purposes. Do not use it for personal, illegal, or unethical activities. Any use that violates company policy or local laws is strictly prohibited and liable for action.</li>
              <li>All activities are monitored to ensure compliance with internal policies and security standards.</li>
              <li>Before using AI-generated content, Users must independently carefully review and verify its accuracy, completeness, lack of bias and adherence to Company standards as Employees/Users are ultimately responsible their work product.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              4. Suspension and Termination of Access
            </Typography>
            <p className="text-base">Dabur reserves the right to suspend or permanently terminate a user's access to Dabur GPT if:</p>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>These Terms are violated</li>
              <li>Tool security or performance is compromised</li>
              <li>Unauthorized or unethical usage is detected</li>
              <li>Report any suspected data breaches or policy violations immediately to the IT department.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              5. Intellectual Property and Ownership
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Unless content includes third-party licensed inputs, all outputs generated through Dabur GPT are the exclusive intellectual property of Dabur India Limited.</li>
              <li>In no event shall Dabur India Limited or its affiliates be liable for any direct, indirect, incidental, special, consequential, or punitive damages, whether in an action in contract, tort (including negligence), or otherwise, arising from your access to or use of the Dabur GPT AI tool.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              6. Platform Updates and Technical Information
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Dabur India Limited may periodically update Dabur GPT's features, functionalities, or these Terms to enhance platform performance and user experience.</li>
              <li>Users will be notified of material updates through official internal communication channels by Dabur's IT policy.</li>
              <li>For platform-related support, users may contact <Link href="mailto:kartikeya.pandey@dabur.com" color="primary">kartikeya.pandey@dabur.com</Link>.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              7. Our Commitment to You
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Dabur GPT is built to support responsible innovation, learning, and collaboration across the organization.</li>
              <li>Dabur encourages employees to use this tool to drive creativity and efficiency, while upholding the highest standards of: Integrity, Lawfulness, Confidentiality, Respect for Dabur's legacy and brand.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              Do's:
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Use the tool for official, work-related purposes only.</li>
              <li>Utilize the tool to enhance productivity, support decision-making, generate content, or conduct research relevant to business goals.</li>
              <li>Always review and Verify AI-generated information before use.</li>
              <li>Ensure data inputs do not violate Dabur's Policies, cybersecurity, or employee conduct policies.</li>
              <li>Kindly comply with technical limitations of the platform.</li>
              <li>A maximum of three documents may be uploaded per session.</li>
              <li>Dabur GPT references only the last five messages when responding.</li>
              <li>Provide a fresh prompt for each image request to avoid carry-over errors.</li>
              <li>For video generation, upload a contextually relevant image for accurate results.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              Don'ts:
            </Typography>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Do not input or upload confidential, sensitive, or restricted Dabur data, such as proprietary formulations, legal contracts, or personal data, unless explicitly authorised.</li>
              <li>Do not use the tool for personal, non-business, or recreational purposes.</li>
              <li>Do not rely on AI-generated content as the final version without human verification, especially in matters of compliance, branding, or legal significance.</li>
              <li>Do not disclose your login credentials or permit unauthorized users to access the platform.</li>
              <li>Do not create or disseminate deceptive, offensive, defamatory, or misleading content, including content that violates laws or Dabur's ethical policies.</li>
              <li>Do not expect Dabur GPT to edit or modify previously generated images. The tool currently does not support alterations to past image outputs once they have been generated.</li>
            </ul>

            <Typography variant="h6" className="mt-6 font-bold">
              Privacy Notice – Dabur GPT
            </Typography>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              1. Introduction
            </Typography>
            <p className="text-base">
              This Privacy Notice is intended to describe the practices Dabur India Limited (hereinafter referred as "Dabur", or "The Company") follows in relation to Dabur GPT ("Tool") with respect to the privacy of all individuals whose personal data is processed and stored in the Tool. Please read this Privacy Notice carefully.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              2. Who manages the Tool?
            </Typography>
            <p className="text-base">
              Dabur and can determine the purposes and means for data processing in its own right (i.e., act as a Data Fiduciary or Data Processor).
            </p>
            <p className="text-base">
              The Tool is hosted internally hosted on Dabur servers, ensuring that all personal information remains within the Company's infrastructure and under its control.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              3. How does the Tool process personal data?
            </Typography>
            <p className="text-base">
              Dabur GPT LLM (Large Language Model) is an artificial intelligence system is aligned to OpenAI's LLM (Large Language Model) and designed to converse with humans in a natural language, simulating human-to-human conversation, born to enable Dabur internal users to experience GPT capabilities within a secure environment, to reduce the usage of opensource ChatGPT/ Gemini and therefore to protect Dabur's brand and data.
            </p>
            <p className="text-base">Your data processed in the Tool is used as follows:</p>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>For the authentication / authorization of the user in the application as a security measure.</li>
              <li>Your data detailed under Section 4 below is processed to provide and allow you to leverage the different capabilities that integrate Dabur GPT and to improve the user's experience while interacting with it.</li>
              <li>Personal data is processed through the LLM by using querying the personal data which resides inside the documents, data, and information of our skills. Personal Data is used for user authentication plus Personal Data may be processed as part of the prompting.</li>
            </ul>
            <p className="text-base">
              Dabur relies on the following basis to legitimize the processing of your personal data in the Tool:
            </p>
            <br/>
            <p className="text-base">
              Processing of your personal data is necessary for the specific purpose(s); to execute a chat function for all use cases where Dabur internal members want to engage with LLM technology in a chat setting. The Tool offers an internal chat function and service that can be used by Dabur's employees and is intended to replace the use of any and all public services that Dabur members might have been using prior to the availability of Dabur GPT.
            </p>
            <br/>
            <p className="text-base">
              You have the right to withdraw your consent at any time, on grounds relating to your particular situation, to the processing of personal data concerning you based on the above purpose(s).
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              Data Retention
            </Typography>
            <p className="text-base">
              The data is stored on Dabur servers, ensuring that all personal information remains within the Company's infrastructure and under its control.
            </p>
            <br/>
            <p className="text-base">
              In terms of data retention, the tool retains data based on user needs meaning users have control over how long their data stays in the system. By default, chat data is stored for 30 days to allow users to access recent conversations conveniently. However, users can manually delete their chats at any time, as it is considered their personal data. This flexible retention policy ensures both convenience and privacy.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              4. What type of personal data is processed in the Tool?
            </Typography>
            <p className="text-base">The Tool processes these personal data categories:</p>
            <br/>
            <p className="text-base">
              User authentication / authorization will be handled via Azure Active Directory (AAD), which serves as the identity provider for login and user management. When users authenticate via AAD, their details - like name, email, and other organizational attributes are securely pulled into the tool. This information is not retained within the application.
            </p>
            <br/>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Dabur Department Name - Dabur specific information about the group to which the employee belongs. Example: Sales, Finance, Marketing, IT etc.</li>
              <li>Full Name</li>
              <li>Email</li>
            </ul>
            <br/>
            <p className="text-base">
              A feed from other Dabur systems: personal data (first name, last name, email address) are provided via Azure Active Directory (AAD) during authorization / authentication of the user in the application. Dabur internal users may submit any type of information as prompts to be leveraged by the implemented model as context to generate response outputs. Both prompts / outputs will not be stored in the application as well as the model's capacity to be re-trained or fine-tuned will be disabled to ensure that no data is retained in any way.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              5. Sensitive personal data
            </Typography>
            <p className="text-base">
              Sensitive personal data reveals your racial or ethnic origin, political opinions, religious or philosophical beliefs, trade union membership, genetic data, biometric data, data concerning health or data concerning sex life or sexual orientation.
            </p>
            <br/>
            <p className="text-base">
              Dabur does not intentionally collect any sensitive personal data from you via the Tool. The Tool's intention is not to process such information.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              6. Who can access your Personal Data?
            </Typography>
            <p className="text-base">Your personal data is accessed in the Tool by the following persons/teams:</p>
            <br/>
            <ul className="list-disc pl-6 flex flex-col gap-2">
              <li>Development and Support team: implementing the solution within Dabur, who are implementing the App Registration process for the authentication / authorization which is the main purpose of processing the data records.</li>
              <li>Administrators: Access to all data elements through backend functionality as well as restricted functionality is available through admin functionality, but it requires changes in the backend database versus accessible in an admin-portal type function.</li>
              <li>Users: Access to primary user interface features in which can be queried due to the nature of the system as an LLM chat experience.</li>
            </ul>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              Security of your Personal Data
            </Typography>
            <p className="text-base">
              Access to the personal data is restricted and controlled. Only authorized personnel within the Company such as system administrators or specific roles assigned within the tool have access to this data. These individuals are typically responsible for managing user access, monitoring tool usage, and ensuring compliance with internal security policies. The level of access is governed by role-based permissions, ensuring that personal data is only available to those who need it to perform their official duties.
            </p>

            <Typography variant="subtitle1" className="mt-4 font-bold">
              7. Further information:
            </Typography>
            <p className="text-base">
              By clicking "Agree & Continue," you confirm that you have read, understood, and agree to abide by these Terms and Conditions of Dabur GPT User Policy, and our Privacy Notice, which is incorporated herein by reference. If you disagree, please refrain from using Dabur GPT.
            </p>
          </Typography>

          <div className="flex items-center space-x-2 pt-2">
            <Checkbox
              id="terms"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <Typography variant="body2">
            I understand and agree to the {" "}
              <Link href="#">Terms and Conditions</Link> and{" "}
              <Link href="#">Privacy Policy</Link>
              {" "}[AGREE & CONTINUE].
            </Typography>
          </div>
        </div>

        <Divider />

        <Button
          variant="contained"
          className="text-center"
          onClick={handleAccept}
          disabled={!agreed || loading}
          sx={{ textTransform: "none" }}
          // className="bg-green-700 hover:bg-green-800 text-white font-bold py-2 px-6 rounded-lg text-lg transition duration-300 ease-in-out"
        >
          {loading ? "Accepting..." : "Agree & Continue"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};


DisclaimerDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};